// @ts-nocheck
/**
 * Individual Adult Learning Dashboard
 * Self-paced learning with progress tracking and certifications
 * Epic 75 - Individual Adult Self-Paced Learning
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  ProgressIndicator,
  ProgressStep,
  Button,
  Tag,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Modal,
  TextInput,
  Dropdown,
  Loading,
  InlineNotification,
  StructuredListWrapper,
  StructuredListHead,
  StructuredListBody,
  StructuredListRow,
  StructuredListCell,
  Search,
  Pagination,
  OverflowMenu,
  OverflowMenuItem,
  Breadcrumb,
  BreadcrumbItem,
} from '@carbon/react';
import {
  ChartLineSmooth,
  Award,
  Book, // Changed from BookOpen
  Time,
  User,
  CheckmarkFilled,
  PlayFilled,
  PauseFilled,
  DocumentDownload,
  Share,
  Settings,
  Analytics,
  Recommend,
  Calendar,
  Trophy,
  Education,
} from '@carbon/icons-react';

// Import types
interface LearnerProfile {
  learner_id: string;
  user_id: string;
  learning_goals: {
    primary_goals: string[];
    secondary_goals: string[];
    target_timeline: string;
  };
  skill_level: any;
  learning_preferences: any;
  career_context?: any;
  progress_tracking_preferences?: any;
}

interface LearningSession {
  session_id: string;
  session_type: string;
  completion_status: string;
  progress_percentage: number;
  time_spent_minutes: number;
  session_started: string;
  learning_objectives: any;
}

interface LearningPathway {
  pathway_id: string;
  pathway_name: string;
  subject_area: string;
  description: string;
  estimated_completion_time: number;
  difficulty_progression: any;
  recommendation_score?: number;
  recommendation_reason?: string;
}

interface LearningAnalytics {
  analytics_id: string;
  reporting_period: string;
  learning_metrics: {
    time_spent: number;
    sessions_completed: number;
    concepts_learned: number;
    skills_practiced: number;
  };
  progress_metrics: {
    courses_started: number;
    courses_completed: number;
    pathways_active: number;
    achievements_earned: number;
  };
  performance_metrics: any;
  engagement_metrics: any;
}

const IndividualLearningDashboard: React.FC = () => {
  // State management
  const [profile, setProfile] = useState<LearnerProfile | null>(null);
  const [activeSession, setActiveSession] = useState<LearningSession | null>(null);
  const [pathways, setPathways] = useState<LearningPathway[]>([]);
  const [recommendations, setRecommendations] = useState<LearningPathway[]>([]);
  const [analytics, setAnalytics] = useState<LearningAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState('weekly');
  const [showPathwayModal, setShowPathwayModal] = useState(false);
  const [selectedPathway, setSelectedPathway] = useState<LearningPathway | null>(null);

  // Fetch data on component mount
  useEffect(() => {
    loadDashboardData();
  }, [selectedPeriod]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Simulate API calls - in real implementation, these would be actual API calls
      const mockLearnerId = 'learner-001';

      // Load profile
      const profileData: LearnerProfile = {
        learner_id: mockLearnerId,
        user_id: 'user-001',
        learning_goals: {
          primary_goals: ['Master modern web development', 'Earn industry certifications', 'Build portfolio projects'],
          secondary_goals: ['Learn cloud technologies', 'Improve problem-solving skills'],
          target_timeline: '6 months',
        },
        skill_level: {
          current_skills: { javascript: 7, react: 6, python: 5, databases: 4 },
          target_skills: { javascript: 9, react: 8, python: 7, databases: 7, aws: 6 },
        },
        learning_preferences: {
          learning_style: 'mixed',
          pace: 'self_paced',
          preferred_formats: ['video', 'interactive', 'practice'],
        },
        career_context: {
          current_role: 'Junior Developer',
          target_role: 'Full Stack Developer',
          industry: 'technology',
          career_stage: 'mid_level',
        },
      };

      // Load active session
      const sessionData: LearningSession = {
        session_id: 'session-001',
        session_type: 'study',
        completion_status: 'in_progress',
        progress_percentage: 65,
        time_spent_minutes: 45,
        session_started: new Date(Date.now() - 45 * 60 * 1000).toISOString(),
        learning_objectives: {
          session_goals: ['Complete React hooks tutorial', 'Build sample component'],
          expected_outcomes: ['Understand useState and useEffect', 'Create reusable components'],
        },
      };

      // Load pathways
      const pathwaysData: LearningPathway[] = [
        {
          pathway_id: 'pathway-tech-001',
          pathway_name: 'Professional Software Development',
          subject_area: 'Technology',
          description: 'Comprehensive software development pathway for career advancement',
          estimated_completion_time: 320,
          difficulty_progression: { start_level: 1, end_level: 8 },
        },
        {
          pathway_id: 'pathway-cloud-001',
          pathway_name: 'Cloud Architecture Fundamentals',
          subject_area: 'Technology',
          description: 'Learn cloud computing principles and AWS services',
          estimated_completion_time: 180,
          difficulty_progression: { start_level: 3, end_level: 7 },
        },
      ];

      // Load recommendations
      const recommendationsData: LearningPathway[] = [
        {
          pathway_id: 'pathway-rec-001',
          pathway_name: 'Advanced React Development',
          subject_area: 'Technology',
          description: 'Deep dive into React patterns and performance optimization',
          estimated_completion_time: 120,
          difficulty_progression: { start_level: 5, end_level: 8 },
          recommendation_score: 0.92,
          recommendation_reason: 'Matches your React experience and career goals',
        },
      ];

      // Load analytics
      const analyticsData: LearningAnalytics = {
        analytics_id: 'analytics-001',
        reporting_period: selectedPeriod,
        learning_metrics: {
          time_spent: 25.5,
          sessions_completed: 12,
          concepts_learned: 36,
          skills_practiced: 18,
        },
        progress_metrics: {
          courses_started: 3,
          courses_completed: 1,
          pathways_active: 2,
          achievements_earned: 5,
        },
        performance_metrics: {
          average_scores: { overall: 85 },
          mastery_levels: { overall: 0.75 },
        },
        engagement_metrics: {
          login_frequency: 5,
          session_duration: 127,
          interaction_quality: 0.8,
        },
      };

      setProfile(profileData);
      setActiveSession(sessionData);
      setPathways(pathwaysData);
      setRecommendations(recommendationsData);
      setAnalytics(analyticsData);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartLearning = (pathway: LearningPathway) => {
    setSelectedPathway(pathway);
    setShowPathwayModal(true);
  };

  const handleResumeSession = () => {
    // Navigate to learning session
  };

  const handlePauseSession = () => {
    // Pause current session
    if (activeSession) {
      setActiveSession({ ...activeSession, completion_status: 'paused' });
    }
  };

  const formatTimeSpent = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getProgressColor = (percentage: number): string => {
    if (percentage >= 80) return 'green';
    if (percentage >= 60) return 'yellow';
    return 'blue';
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: '25rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loading withOverlay={false} />
      </div>
    );
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        title="Error loading dashboard"
        subtitle={error}
        style={{ margin: 'var(--cds-spacing-05) 0' }}
      />
    );
  }

  return (
    <div
      className="individual-learning-dashboard cds-dashboard"
      style={{ padding: 'var(--cds-spacing-07)', backgroundColor: 'var(--cds-layer-01)' }}
    >
      {/* Breadcrumb Navigation */}
      <Breadcrumb
        style={{ marginBottom: 'var(--cds-spacing-07)', minHeight: '44px', display: 'flex', alignItems: 'center' }}
      >
        <BreadcrumbItem href="/dashboard">Dashboard</BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>Individual Learning</BreadcrumbItem>
      </Breadcrumb>

      {/* Header Section */}
      <div style={{ marginBottom: 'var(--cds-spacing-07)' }}>
        <h1
          style={{
            fontSize: 'var(--cds-productive-heading-05-font-size)',
            fontWeight: 'var(--cds-productive-heading-05-font-weight)',
            marginBottom: 'var(--cds-spacing-03)',
            color: 'var(--cds-text-primary)',
          }}
        >
          Self-Paced Learning Dashboard
        </h1>
        <p
          style={{
            color: 'var(--cds-text-secondary)',
            fontSize: 'var(--cds-body-long-01-font-size)',
          }}
        >
          Welcome back! Continue your learning journey at your own pace.
        </p>
      </div>

      <Grid>
        {/* Active Session Card */}
        <Column lg={16} md={8} sm={4}>
          {activeSession && activeSession.completion_status === 'in_progress' && (
            <Tile
              className="individual-learning-tile cds-tile"
              style={{ marginBottom: 'var(--cds-spacing-05)', backgroundColor: 'var(--cds-layer-02)' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <h3
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--cds-spacing-03)',
                      fontSize: 'var(--cds-productive-heading-03-font-size)',
                      marginBottom: 'var(--cds-spacing-05)',
                      color: 'var(--cds-text-primary)',
                    }}
                  >
                    <PlayFilled />
                    Active Learning Session
                  </h3>
                  <p
                    style={{
                      color: 'var(--cds-text-secondary)',
                      marginBottom: 'var(--cds-spacing-05)',
                    }}
                  >
                    {activeSession.learning_objectives.session_goals[0]}
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--cds-spacing-05)',
                      marginBottom: 'var(--cds-spacing-05)',
                    }}
                  >
                    <Tag type="blue">{activeSession.session_type}</Tag>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        color: 'var(--cds-text-secondary)',
                        fontSize: 'var(--cds-body-compact-01-font-size)',
                      }}
                    >
                      <Time />
                      {formatTimeSpent(activeSession.time_spent_minutes)}
                    </span>
                  </div>
                  <ProgressIndicator currentIndex={Math.floor(activeSession.progress_percentage / 25)}>
                    <ProgressStep complete={activeSession.progress_percentage > 25} label="Started" />
                    <ProgressStep complete={activeSession.progress_percentage > 50} label="In Progress" />
                    <ProgressStep complete={activeSession.progress_percentage > 75} label="Nearly Done" />
                    <ProgressStep complete={activeSession.progress_percentage === 100} label="Complete" />
                  </ProgressIndicator>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <Button
                    className="cds-button sigma08-primary-button"
                    kind="primary"
                    renderIcon={PlayFilled}
                    onClick={handleResumeSession}
                  >
                    Resume
                  </Button>
                  <Button
                    className="cds-button sigma08-secondary-button"
                    kind="secondary"
                    renderIcon={PauseFilled}
                    onClick={handlePauseSession}
                  >
                    Pause
                  </Button>
                </div>
              </div>
            </Tile>
          )}
        </Column>

        {/* Analytics Overview */}
        <Column lg={4} md={4} sm={4}>
          <Tile
            className="individual-learning-tile cds-tile"
            style={{
              marginBottom: 'var(--cds-spacing-05)',
              textAlign: 'center',
              backgroundColor: 'var(--cds-layer-02)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--cds-spacing-03)',
                marginBottom: 'var(--cds-spacing-05)',
              }}
            >
              <Time style={{ color: 'var(--cds-icon-primary)' }} />
              <h4 style={{ color: 'var(--cds-text-primary)' }}>Time Spent</h4>
            </div>
            <p
              style={{
                fontSize: 'var(--cds-productive-heading-04-font-size)',
                fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                color: 'var(--cds-text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              {analytics?.learning_metrics.time_spent}h
            </p>
            <p
              style={{
                color: 'var(--cds-text-secondary)',
                fontSize: 'var(--cds-body-compact-01-font-size)',
              }}
            >
              This {selectedPeriod}
            </p>
          </Tile>
        </Column>

        <Column lg={4} md={4} sm={4}>
          <Tile
            className="individual-learning-tile cds-tile"
            style={{
              marginBottom: 'var(--cds-spacing-05)',
              textAlign: 'center',
              backgroundColor: 'var(--cds-layer-02)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--cds-spacing-03)',
                marginBottom: 'var(--cds-spacing-05)',
              }}
            >
              <CheckmarkFilled style={{ color: 'var(--cds-support-success)' }} />
              <h4 style={{ color: 'var(--cds-text-primary)' }}>Sessions</h4>
            </div>
            <p
              style={{
                fontSize: 'var(--cds-productive-heading-04-font-size)',
                fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                color: 'var(--cds-text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              {analytics?.learning_metrics.sessions_completed}
            </p>
            <p
              style={{
                color: 'var(--cds-text-secondary)',
                fontSize: 'var(--cds-body-compact-01-font-size)',
              }}
            >
              Completed
            </p>
          </Tile>
        </Column>

        <Column lg={4} md={4} sm={4}>
          <Tile
            className="individual-learning-tile cds-tile"
            style={{
              marginBottom: 'var(--cds-spacing-05)',
              textAlign: 'center',
              backgroundColor: 'var(--cds-layer-02)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--cds-spacing-03)',
                marginBottom: 'var(--cds-spacing-05)',
              }}
            >
              <Book style={{ color: 'var(--cds-icon-primary)' }} />
              <h4 style={{ color: 'var(--cds-text-primary)' }}>Concepts</h4>
            </div>
            <p
              style={{
                fontSize: 'var(--cds-productive-heading-04-font-size)',
                fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                color: 'var(--cds-text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              {analytics?.learning_metrics.concepts_learned}
            </p>
            <p
              style={{
                color: 'var(--cds-text-secondary)',
                fontSize: 'var(--cds-body-compact-01-font-size)',
              }}
            >
              Learned
            </p>
          </Tile>
        </Column>

        <Column lg={4} md={4} sm={4}>
          <Tile
            className="individual-learning-tile cds-tile"
            style={{
              marginBottom: 'var(--cds-spacing-05)',
              textAlign: 'center',
              backgroundColor: 'var(--cds-layer-02)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--cds-spacing-03)',
                marginBottom: 'var(--cds-spacing-05)',
              }}
            >
              <Trophy style={{ color: 'var(--cds-support-warning)' }} />
              <h4 style={{ color: 'var(--cds-text-primary)' }}>Achievements</h4>
            </div>
            <p
              style={{
                fontSize: 'var(--cds-productive-heading-04-font-size)',
                fontWeight: 'var(--cds-productive-heading-04-font-weight)',
                color: 'var(--cds-text-primary)',
                marginBottom: '0.5rem',
              }}
            >
              {analytics?.progress_metrics.achievements_earned}
            </p>
            <p
              style={{
                color: 'var(--cds-text-secondary)',
                fontSize: 'var(--cds-body-compact-01-font-size)',
              }}
            >
              Earned
            </p>
          </Tile>
        </Column>

        {/* Recommended Pathways */}
        <Column lg={8} md={8} sm={4}>
          <Tile
            className="individual-learning-tile cds-tile"
            style={{
              marginBottom: 'var(--cds-spacing-05)',
              backgroundColor: 'var(--cds-layer-02)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--cds-spacing-03)',
                marginBottom: 'var(--cds-spacing-06)',
              }}
            >
              <Recommend style={{ color: 'var(--cds-icon-primary)' }} />
              <h3
                style={{
                  fontSize: 'var(--cds-productive-heading-03-font-size)',
                  color: 'var(--cds-text-primary)',
                }}
              >
                Recommended for You
              </h3>
            </div>

            {recommendations.map(pathway => (
              <div
                key={pathway.pathway_id}
                style={{
                  border: '1px solid var(--cds-border-subtle-01)',
                  borderRadius: '4px',
                  padding: '1rem',
                  marginBottom: 'var(--cds-spacing-05)',
                  backgroundColor: 'var(--cds-layer-01)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '0.5rem',
                  }}
                >
                  <h4
                    style={{
                      color: 'var(--cds-text-primary)',
                      fontSize: 'var(--cds-productive-heading-02-font-size)',
                    }}
                  >
                    {pathway.pathway_name}
                  </h4>
                  <Tag type="green" size="sm">
                    {Math.round((pathway.recommendation_score || 0) * 100)}% match
                  </Tag>
                </div>
                <p
                  style={{
                    color: 'var(--cds-text-secondary)',
                    marginBottom: 'var(--cds-spacing-05)',
                    fontSize: 'var(--cds-body-compact-01-font-size)',
                  }}
                >
                  {pathway.description}
                </p>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      gap: 'var(--cds-spacing-05)',
                      fontSize: 'var(--cds-body-compact-01-font-size)',
                      color: 'var(--cds-text-secondary)',
                    }}
                  >
                    <span>{pathway.estimated_completion_time}h</span>
                    <span>{pathway.subject_area}</span>
                  </div>
                  <Button
                    className="cds-button sigma08-primary-button"
                    kind="primary"
                    onClick={() => handleStartLearning(pathway)}
                  >
                    Start Learning
                  </Button>
                </div>
                {pathway.recommendation_reason && (
                  <p
                    style={{
                      fontSize: 'var(--cds-helper-text-01-font-size)',
                      color: 'var(--cds-support-info)',
                      marginTop: '0.5rem',
                      fontStyle: 'italic',
                    }}
                  >
                    {pathway.recommendation_reason}
                  </p>
                )}
              </div>
            ))}
          </Tile>
        </Column>

        {/* Learning Goals */}
        <Column lg={8} md={8} sm={4}>
          <Tile
            className="individual-learning-tile cds-tile"
            style={{
              marginBottom: 'var(--cds-spacing-05)',
              backgroundColor: 'var(--cds-layer-02)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--cds-spacing-03)',
                marginBottom: 'var(--cds-spacing-06)',
              }}
            >
              <Education style={{ color: 'var(--cds-icon-primary)' }} />
              <h3
                style={{
                  fontSize: 'var(--cds-productive-heading-03-font-size)',
                  color: 'var(--cds-text-primary)',
                }}
              >
                Your Learning Goals
              </h3>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <h4
                style={{
                  color: 'var(--cds-text-primary)',
                  marginBottom: 'var(--cds-spacing-03)',
                  fontSize: 'var(--cds-productive-heading-02-font-size)',
                }}
              >
                Primary Goals
              </h4>
              {profile?.learning_goals.primary_goals.map((goal, index) => (
                <div
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--cds-spacing-03)',
                    marginBottom: '0.5rem',
                  }}
                >
                  <CheckmarkFilled size={16} style={{ color: 'var(--cds-support-success)' }} />
                  <span style={{ color: 'var(--cds-text-primary)' }}>{goal}</span>
                </div>
              ))}
            </div>

            <div>
              <h4
                style={{
                  color: 'var(--cds-text-primary)',
                  marginBottom: 'var(--cds-spacing-03)',
                  fontSize: 'var(--cds-productive-heading-02-font-size)',
                }}
              >
                Target Timeline
              </h4>
              <Tag type="blue">{profile?.learning_goals.target_timeline}</Tag>
            </div>
          </Tile>
        </Column>

        {/* Available Pathways */}
        <Column lg={16} md={8} sm={4}>
          <Tile
            className="individual-learning-tile cds-tile"
            style={{
              marginBottom: 'var(--cds-spacing-05)',
              backgroundColor: 'var(--cds-layer-02)',
              maxWidth: '100%',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--cds-spacing-06)',
                flexWrap: 'wrap',
                gap: 'var(--cds-spacing-05)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <Book style={{ color: 'var(--cds-icon-primary)' }} />
                <h3
                  style={{
                    fontSize: 'var(--cds-productive-heading-03-font-size)',
                    color: 'var(--cds-text-primary)',
                  }}
                >
                  Available Learning Pathways
                </h3>
              </div>
              <Search
                labelText="Search available learning pathways"
                placeholder="Search pathways..."
                style={{ width: '100%', maxWidth: '300px', minHeight: '44px' }}
              />
            </div>

            {/* Mobile Card View - displays below 672px (md breakpoint) */}
            <div
              className="pathways-mobile-view"
              style={{
                display: 'block',
              }}
            >
              <style>{`
                @media (min-width: 672px) {
                  .pathways-mobile-view {
                    display: none !important;
                  }
                }
                @media (max-width: 671px) {
                  .pathways-desktop-view {
                    display: none !important;
                  }
                }
              `}</style>
              {pathways.map(pathway => (
                <div
                  key={pathway.pathway_id}
                  style={{
                    border: '1px solid var(--cds-border-subtle-01)',
                    borderRadius: '4px',
                    padding: 'var(--cds-spacing-05)',
                    marginBottom: 'var(--cds-spacing-05)',
                    backgroundColor: 'var(--cds-layer-01)',
                    maxWidth: '100%',
                  }}
                >
                  <h4
                    style={{
                      color: 'var(--cds-text-primary)',
                      fontSize: 'var(--cds-productive-heading-02-font-size)',
                      marginBottom: 'var(--cds-spacing-03)',
                    }}
                  >
                    {pathway.pathway_name}
                  </h4>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--cds-spacing-03)',
                      marginBottom: 'var(--cds-spacing-05)',
                    }}
                  >
                    <Tag type="blue" size="sm">
                      {pathway.subject_area}
                    </Tag>
                    <Tag type="gray" size="sm">
                      {pathway.estimated_completion_time}h
                    </Tag>
                    <Tag type="gray" size="sm">
                      Level {pathway.difficulty_progression?.start_level}-{pathway.difficulty_progression?.end_level}
                    </Tag>
                  </div>
                  <p
                    style={{
                      color: 'var(--cds-text-secondary)',
                      marginBottom: 'var(--cds-spacing-05)',
                      fontSize: 'var(--cds-body-compact-01-font-size)',
                    }}
                  >
                    {pathway.description}
                  </p>
                  <Button
                    className="cds-button sigma08-primary-button"
                    kind="primary"
                    size="sm"
                    onClick={() => handleStartLearning(pathway)}
                    style={{ width: '100%', minHeight: '44px' }}
                  >
                    View Details
                  </Button>
                </div>
              ))}
            </div>

            {/* Desktop Table View - displays at 672px and above */}
            <div
              className="pathways-desktop-view"
              style={{
                display: 'block',
                maxWidth: '100%',
                overflowX: 'auto',
              }}
            >
              <DataTable
                rows={pathways.map(pathway => ({
                  id: pathway.pathway_id,
                  name: pathway.pathway_name,
                  subject: pathway.subject_area,
                  duration: `${pathway.estimated_completion_time}h`,
                  difficulty: `Level ${pathway.difficulty_progression?.start_level}-${pathway.difficulty_progression?.end_level}`,
                  description: pathway.description,
                }))}
                headers={[
                  { key: 'name', header: 'Pathway Name' },
                  { key: 'subject', header: 'Subject Area' },
                  { key: 'duration', header: 'Duration' },
                  { key: 'difficulty', header: 'Difficulty' },
                  { key: 'actions', header: 'Actions' },
                ]}
              >
                {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
                  <TableContainer style={{ maxWidth: '100%' }}>
                    <Table {...getTableProps()} style={{ tableLayout: 'fixed', width: '100%' }}>
                      <TableHead>
                        <TableRow>
                          {headers.map(header => (
                            <TableHeader {...getHeaderProps({ header })}>{header.header}</TableHeader>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map(row => (
                          <TableRow {...getRowProps({ row })}>
                            {row.cells.map(cell => (
                              <TableCell key={cell.id}>
                                {cell.info.header === 'actions' ? (
                                  <Button
                                    className="cds-button"
                                    kind="ghost"
                                    onClick={() => {
                                      const pathway = pathways.find(p => p.pathway_id === row.id);
                                      if (pathway) handleStartLearning(pathway);
                                    }}
                                  >
                                    View Details
                                  </Button>
                                ) : (
                                  cell.value
                                )}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DataTable>
            </div>
          </Tile>
        </Column>
      </Grid>

      {/* Pathway Details Modal */}
      <Modal
        open={showPathwayModal}
        onRequestClose={() => setShowPathwayModal(false)}
        modalHeading={selectedPathway?.pathway_name || 'Learning Pathway'}
        primaryButtonText="Start Pathway"
        secondaryButtonText="Cancel"
        onRequestSubmit={() => {
          setShowPathwayModal(false);
        }}
      >
        {selectedPathway && (
          <div>
            <p style={{ marginBottom: '1rem' }}>{selectedPathway.description}</p>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                marginBottom: 'var(--cds-spacing-05)',
              }}
            >
              <Tag type="blue">{selectedPathway.subject_area}</Tag>
              <Tag type="gray">{selectedPathway.estimated_completion_time} hours</Tag>
            </div>
            <p
              style={{
                color: 'var(--cds-text-secondary)',
                fontSize: 'var(--cds-body-compact-01-font-size)',
              }}
            >
              This pathway will take approximately {selectedPathway.estimated_completion_time} hours to complete and
              covers difficulty levels {selectedPathway.difficulty_progression?.start_level} to{' '}
              {selectedPathway.difficulty_progression?.end_level}.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default IndividualLearningDashboard;
